import {mcpDuplicateConnectionMessage} from './mcp-provider-message';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { mcpAccountLinkConfig as config } from './mcp-embed.config';

/** Isolated OAuth relay: never reads or replaces the ordinary Mango login. */
@Injectable({ providedIn: 'root' })
export class McpProviderConnectionService {
  private readonly original = new URL(location.href);
  private readonly key = 'mango-mcp-provider:';
  private readonly enabled = config.enabled && window.parent === window &&
    location.origin === environment.webAppUrl;
  // Own only explicit connector launch/return routes. Ordinary Google/Outlook
  // callbacks continue through the existing web app when they lack MCP state.
  readonly active = this.enabled && (new URLSearchParams(this.original.hash.slice(1)).has('mcpProvider') ||
    (this.original.searchParams.get('state') || '').startsWith('mcp_') || this.original.hash === '#mcpProviderResult');
  status = 'Opening your account connection…';
  returnUrl: string | null = null;
  credentialSession: {connectionRef:string;receipt:string;authToken:string;mirrorId:number;widgetId:number;mangoAccount:string} | null = null;
  completed = false;
  finished = false;
  private safeReturn(value: unknown): string | null {
    try { const u = new URL(String(value)); return u.origin === 'https://chatgpt.com' && !u.username && !u.password ? u.href : null; }
    catch { return null; }
  }
  private async post(path: string, body: unknown): Promise<any> {
    const r = await fetch(config.mcpOrigin + path, {method:'POST',credentials:'omit',redirect:'error',cache:'no-store',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)});
    if (!r.ok) throw new Error('Connection unavailable');
    return r.json();
  }
  async finishCredentials(accountId?:number):Promise<void>{
    const session=this.credentialSession;if(!session)return;
    this.credentialSession=null;
    this.status='Checking your calendar connection…';
    try{
      const r=await this.post('/provider/credential-complete',{connectionRef:session.connectionRef,receipt:session.receipt,...(accountId?{accountId}:{cancelled:true})});
      this.completed=r.status==='connected';
      this.status=this.completed?'Apple iCloud Calendar connected successfully!'+(r.accountLabel?' Connected as '+r.accountLabel+'.':'')+' Close this tab to return to your chat.':r.status==='cancelled'?'Connection cancelled.':'We could not confirm the connection. Return to chat to check connected calendars before trying again.';
    }catch{this.status='We could not confirm the connection. Return to chat to check connected calendars before trying again.';}
    finally{session.authToken='';this.finished=true;}
  }
  async open(): Promise<boolean> {
    if (!this.active) return false;
    history.replaceState(null, '', '/#mcpProviderResult');
    try {
      if (this.original.hash === '#mcpProviderResult') {
        this.finished = true;
        this.status = 'Return to your chat and check the connection status. Start a fresh connection from chat if needed.';
        return true;
      }
      const ticket = new URLSearchParams(this.original.hash.slice(1)).get('mcpProvider');
      if (ticket) {
        if (!/^[A-Za-z0-9_-]{43}$/.test(ticket)) throw new Error();
        this.returnUrl = this.safeReturn(this.original.searchParams.get('redirectUrl'));
        const r = await this.post('/provider/begin', {ticket});
        if(r.mode==='icloud'){
          if(!/^[A-Za-z0-9_-]{43}$/.test(r.connectionRef)||!/^[A-Za-z0-9_-]{43}$/.test(r.receipt)||typeof r.authToken!=='string'||!r.authToken||!Number.isSafeInteger(r.widgetId)||!Number.isSafeInteger(r.mirrorId))throw new Error();
          // Memory only: never replace the browser's ordinary Mango account.
          this.credentialSession=r;
          this.status='Connect Apple iCloud Calendar to Mango account '+r.mangoAccount+'.';
          return true;
        }
        const target = new URL(r.authorizationUrl);
        if (target.protocol !== 'https:' || !['accounts.google.com','login.microsoftonline.com','todoist.com'].includes(target.hostname) || target.port || target.username || target.password || !/^mcp_[A-Za-z0-9_-]{43}$/.test(r.state) || target.searchParams.get('state') !== r.state) throw new Error();
        // Keep the return receipt in this browser tab across provider navigation.
        // It contains no provider tokens. A missing/expired receipt fails closed;
        // do not silently fall back to whichever Mango account is open elsewhere.
        sessionStorage.setItem(this.key+r.state, JSON.stringify({connectionRef:r.connectionRef,receipt:r.receipt,state:r.state,returnUrl:this.returnUrl,expires:Date.now()+1800000}));
        this.status = 'Opening '+r.provider+' for sign-in and approval…';
        location.assign(target.href);
        return true;
      }
      const state = this.original.searchParams.get('state');
      if (!/^mcp_[A-Za-z0-9_-]{43}$/.test(state || '')) throw new Error();
      const key = this.key+state;
      const pending = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!pending || pending.expires <= Date.now() || pending.state !== state) throw new Error();
      // Consume before submitting: an OAuth code must not be replayed following
      // a timeout/500 because the provider/backend may already have used it.
      sessionStorage.removeItem(key);
      this.returnUrl = this.safeReturn(pending.returnUrl);
      this.status = 'Finishing your connection. Please keep this page open…';
      const r = await this.post('/provider/complete', {connectionRef:pending.connectionRef,receipt:pending.receipt,state,code:this.original.searchParams.get('code'),error:this.original.searchParams.get('error'),callbackPath:this.original.pathname,callbackQuery:this.original.search});
      // Only verified success earns the success screen. The known duplicate
      // rejection means an account already exists, NOT that this widget selected
      // its calendars. Unknown failures remain uncertain and require inspection.
      this.completed = r.status === 'connected';
      this.finished = true;
      this.status = this.completed ? r.provider+' connected successfully!'+(r.accountLabel?' Connected as '+r.accountLabel+'.':'')+' Close this tab to return to your chat.' : r.reason === 'already_connected' ? mcpDuplicateConnectionMessage(r.provider) : r.status === 'cancelled' ? 'Connection cancelled. Return to chat whenever you are ready.' : 'We could not confirm the connection. Return to chat to check its status, or review connected accounts in the Mango web app before trying again.';
    } catch {
      this.finished = true;
      this.status = 'This connection could not finish here. Return to your existing chat and ask to check the connection or open a fresh connection link.';
    }
    return true;
  }
}
